/**
 * TemplateManager — CRUD interface for prompt templates.
 *
 * Lists all templates, allows creating new ones, editing content,
 * and toggling active status. Uses the promptTemplates Convex table.
 */
import { useState, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useErrorToast } from "../dashboard/App";

// --- Create / Edit form ------------------------------------------------------

function TemplateForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: { name: string; content: string; version: number; active: boolean };
  onSave: (data: { name: string; content: string; version: number; active: boolean }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [content, setContent] = useState(initial?.content ?? "");
  const [version, setVersion] = useState(initial?.version ?? 1);
  const [active, setActive] = useState(initial?.active ?? true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !content.trim()) return;
    onSave({ name: name.trim(), content: content.trim(), version, active });
  };

  const inputCls =
    "w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-indigo-500 focus:outline-none";

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
      <h3 className="text-sm font-semibold text-zinc-100">
        {initial ? "Edit Template" : "New Template"}
      </h3>

      <div>
        <label className="block text-xs text-zinc-500 mb-1">Name</label>
        <input
          className={inputCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. minion-v1-execution"
        />
      </div>

      <div>
        <label className="block text-xs text-zinc-500 mb-1">Version</label>
        <input
          type="number"
          min={1}
          className={`${inputCls} w-24`}
          value={version}
          onChange={(e) => setVersion(Number(e.target.value) || 1)}
        />
      </div>

      <div>
        <label className="block text-xs text-zinc-500 mb-1">Content</label>
        <textarea
          className={`${inputCls} min-h-[200px] font-mono text-xs`}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Template content..."
          rows={12}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="template-active"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="accent-indigo-500"
        />
        <label htmlFor="template-active" className="text-sm text-zinc-300">
          Active
        </label>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving || !name.trim() || !content.trim()}
          className="px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-40"
        >
          {saving ? "Saving..." : initial ? "Update" : "Create"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Template card -----------------------------------------------------------

function TemplateCard({
  template,
  onEdit,
  onToggle,
  toggling,
}: {
  template: {
    _id: Id<"promptTemplates">;
    name: string;
    content: string;
    version: number;
    createdBy?: string;
    createdAt: number;
    active: boolean;
  };
  onEdit: () => void;
  onToggle: () => void;
  toggling: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-zinc-500 hover:text-zinc-300 transition-colors shrink-0"
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-zinc-100 truncate">{template.name}</span>
          <span className="text-xs text-zinc-500 font-mono shrink-0">v{template.version}</span>
          <span
            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${
              template.active
                ? "bg-green-500/10 border-green-500/20 text-green-400"
                : "bg-zinc-800 border-zinc-700 text-zinc-500"
            }`}
          >
            {template.active ? "active" : "inactive"}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onToggle}
            disabled={toggling}
            className="px-2.5 py-1 text-xs rounded border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-40"
          >
            {toggling ? "..." : template.active ? "Deactivate" : "Activate"}
          </button>
          <button
            onClick={onEdit}
            className="px-2.5 py-1 text-xs rounded border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            Edit
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-zinc-500">
        {template.createdBy && <span>by {template.createdBy}</span>}
        <span>{new Date(template.createdAt).toLocaleDateString()}</span>
        <span className="font-mono text-zinc-600">{template.content.length} chars</span>
      </div>

      {expanded && (
        <div className="bg-zinc-950 border border-zinc-800 rounded p-3 mt-2">
          <pre className="text-xs text-zinc-400 font-mono whitespace-pre-wrap break-words max-h-[400px] overflow-y-auto">
            {template.content}
          </pre>
        </div>
      )}
    </div>
  );
}

// --- Main TemplateManager ----------------------------------------------------

export function TemplateManager() {
  const templates = useQuery(api.promptTemplates.listTemplates);
  const createTemplate = useMutation(api.promptTemplates.createTemplate);
  const updateTemplate = useMutation(api.promptTemplates.updateTemplate);
  const showError = useErrorToast();

  const [mode, setMode] = useState<"list" | "create" | "edit">("list");
  const [editingId, setEditingId] = useState<Id<"promptTemplates"> | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<Id<"promptTemplates"> | null>(null);

  const editingTemplate = editingId ? templates?.find((t) => t._id === editingId) : null;

  const handleCreate = useCallback(
    async (data: { name: string; content: string; version: number; active: boolean }) => {
      setSaving(true);
      try {
        await createTemplate(data);
        setMode("list");
      } catch (err: any) {
        showError(err.message || "Failed to create template");
      } finally {
        setSaving(false);
      }
    },
    [createTemplate, showError],
  );

  const handleUpdate = useCallback(
    async (data: { name: string; content: string; version: number; active: boolean }) => {
      if (!editingId) return;
      setSaving(true);
      try {
        await updateTemplate({ id: editingId, ...data });
        setMode("list");
        setEditingId(null);
      } catch (err: any) {
        showError(err.message || "Failed to update template");
      } finally {
        setSaving(false);
      }
    },
    [editingId, updateTemplate, showError],
  );

  const handleToggle = useCallback(
    async (id: Id<"promptTemplates">, currentActive: boolean) => {
      setTogglingId(id);
      try {
        await updateTemplate({ id, active: !currentActive });
      } catch (err: any) {
        showError(err.message || "Failed to toggle template");
      } finally {
        setTogglingId(null);
      }
    },
    [updateTemplate, showError],
  );

  const loading = templates === undefined;

  if (mode === "create") {
    return (
      <TemplateForm
        onSave={handleCreate}
        onCancel={() => setMode("list")}
        saving={saving}
      />
    );
  }

  if (mode === "edit" && editingTemplate) {
    return (
      <TemplateForm
        initial={{
          name: editingTemplate.name,
          content: editingTemplate.content,
          version: editingTemplate.version,
          active: editingTemplate.active,
        }}
        onSave={handleUpdate}
        onCancel={() => {
          setMode("list");
          setEditingId(null);
        }}
        saving={saving}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100">Prompt Templates</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            Manage system prompts for agent sessions.
          </p>
        </div>
        <button
          onClick={() => setMode("create")}
          className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
        >
          New template
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-600">Loading...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-zinc-600">
          <p className="text-sm">No templates yet.</p>
          <p className="text-xs text-zinc-500 mt-1">Create a prompt template to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => (
            <TemplateCard
              key={template._id}
              template={template}
              onEdit={() => {
                setEditingId(template._id);
                setMode("edit");
              }}
              onToggle={() => handleToggle(template._id, template.active)}
              toggling={togglingId === template._id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
